import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiEdit, FiTrash2, FiPlus, FiEye, FiAward } from 'react-icons/fi';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '../../../components/ui/card';
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from '../../../components/ui/table';
import Modal from '../../../components/admin/common/Modal';
import adminApi from '../../../services/adminApi';
import AwardCoinsDialog from '../../../components/admin/mad-coins/AwardCoinsDialog';

const CustomerListPage = () => {
  const [customers, setCustomers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [awardCoinsModalOpen, setAwardCoinsModalOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const navigate = useNavigate();

  // Fetch customers
  const fetchCustomers = async () => {
    setIsLoading(true);
    try {
      const response = await adminApi.getUsers(); // The API endpoint is still /users
      if (response.success) {
        setCustomers(response.users || []);
      } else {
        setError(response.error || 'Failed to fetch customers');
      }
    } catch (err) {
      setError(err.toString());
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomers();
  }, []);

  // Handle navigation
  const handleAddClick = () => {
    navigate('/admin/customers/new');
  };

  const handleEditClick = (customer, e) => {
    if (e) e.stopPropagation();
    navigate(`/admin/customers/edit/${customer._id}`);
  };

  const handleViewClick = (customer, e) => {
    if (e) e.stopPropagation();
    navigate(`/admin/customers/view/${customer._id}`);
  };

  // Handle delete
  const openDeleteModal = (customer, e) => {
    if (e) e.stopPropagation();
    setSelectedCustomer(customer);
    setDeleteModalOpen(true);
  };

  const handleDeleteCustomer = async () => {
    if (!selectedCustomer) return;
    try {
      await adminApi.deleteUser(selectedCustomer._id);
      fetchCustomers(); // Refresh the list
      setDeleteModalOpen(false);
    } catch (err) {
      setError(err.toString());
    }
  };

  const handleAwardCoins = async (userId, coinData) => {
    try {
      await adminApi.awardFreeCoins(userId, coinData);
      fetchCustomers(); // Refresh the list
    } catch (err) {
      setError(err.toString());
    }
  };

  return (
    <div className="p-6">
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle>Customers</CardTitle>
            <Button onClick={handleAddClick}>
              <FiPlus className="mr-2 h-4 w-4" /> Add Customer
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {error && (
            <div className="bg-red-100 text-red-700 p-3 rounded mb-4">
              Error: {error}
            </div>
          )}
          {isLoading ? (
            <div>Loading...</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Verified</TableHead>
                  <TableHead>Registered On</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {customers.map((customer) => (
                  <TableRow key={customer._id}>
                    <TableCell>{`${customer.firstName} ${customer.lastName}`}</TableCell>
                    <TableCell>{customer.email}</TableCell>
                    <TableCell>{customer.phone || '-'}</TableCell>
                    <TableCell>
                      <Badge variant={customer.isVerified ? 'success' : 'destructive'}>
                        {customer.isVerified ? 'Yes' : 'No'}
                      </Badge>
                    </TableCell>
                    <TableCell>{new Date(customer.createdAt).toLocaleDateString()}</TableCell>
                    <TableCell>
                      <div className="flex space-x-2">
                        <Button variant="ghost" size="icon" onClick={(e) => handleViewClick(customer, e)}>
                          <FiEye className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={(e) => handleEditClick(customer, e)}>
                          <FiEdit className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={(e) => openDeleteModal(customer, e)}>
                          <FiTrash2 className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={(e) => {
                          e.stopPropagation();
                          setSelectedCustomer(customer);
                          setAwardCoinsModalOpen(true);
                        }}>
                          <FiAward className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title="Delete Customer"
        size="sm"
        footer={
          <>
            <Button
              variant="outline"
              onClick={() => setDeleteModalOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={handleDeleteCustomer}
            >
              Delete
            </Button>
          </>
        }
      >
        <p>
          Are you sure you want to delete the customer{' '}
          <strong>{selectedCustomer?.firstName} {selectedCustomer?.lastName}</strong>?
        </p>
        <p className="mt-2 text-sm text-gray-500">
          This action cannot be undone.
        </p>
      </Modal>

      {selectedCustomer && (
        <AwardCoinsDialog
          isOpen={awardCoinsModalOpen}
          onClose={() => setAwardCoinsModalOpen(false)}
          onAward={handleAwardCoins}
          user={selectedCustomer}
        />
      )}
    </div>
  );
};

export default CustomerListPage;
